import moment from 'moment-timezone';
import { getRealTimezoneMoment } from 'utils/timeUtils';
import { SpecialService } from 'sharedTypes/common/specialServices';
import { LoadType } from 'sharedTypes/loads/loadType';
import {
  OfferStatusId,
  OfferHistoryType,
  OfferType
} from 'sharedTypes/offers/offers';

//Constants
import { AppointmentType, LOAD_EQ_SPECIAL_SERVICES_IDS_WITH_VALUE, LoadStatusId, PERMIT_SPECIAL_SERVICE_ID } from 'constants/loadConstants/loadConstants';
import { ROUTES } from 'constants/routeConstants';
//Moment Formats
const SHORT_FORMAT_TIME = 'ddd, MMM D, HH:mm';

const LONG_FORMAT = 'dddd, MMMM D';
const LONG_FORMAT_TIME = 'dddd, MMMM D, HH:mm';

export const getLoadRequirements = (loadDetails: LoadType) => {
  const requirements = [];
  if (loadDetails.IsBlind) {
    requirements.push('Blind');
  }
  if (loadDetails.FreezeProtect) {
    requirements.push('Freeze');
  }
  if (loadDetails.IsHazmat) {
    requirements.push('Hazmat');
  }
  if (loadDetails.IsPermit) {
    requirements.push('Permit');
  }
  if (loadDetails.SealRequired) {
    requirements.push('Seal');
  }
  if (loadDetails.TankerEndorsement) {
    requirements.push('Tanker Endorsed');
  }
  if (loadDetails.IsTeam) {
    requirements.push('Team');
  }
  if (loadDetails.TwicNeeded) {
    requirements.push('TWIC');
  }
  if (loadDetails.DropTrailer || loadDetails.PickupApptTypeId === AppointmentType.DropTrailer || loadDetails.DeliveryApptTypeId === AppointmentType.DropTrailer) {
    requirements.push('Drop Trailer');
  }

  return requirements.join(', ') || false;
}

export const getSpecialEquipment = (loadDetails: LoadType) => {
  const specialEquipment = [];

  if (loadDetails.FoodGradeRequired) {
    specialEquipment.push('Food Grade');
  }
  if (loadDetails.HighCubeRequired) {
    specialEquipment.push('High Cube');
  }
  if (loadDetails.LiftGateRequired) {
    specialEquipment.push('Lift Gate');
  }
  if (loadDetails.LoadBarsRequired) {
    specialEquipment.push('Load Bars');
  }
  if (loadDetails.PlateTrailerRequired) {
    specialEquipment.push('Plate Trailer')
  }
  if (loadDetails.RollDoorsRequired) {
    specialEquipment.push('Roll Doors');
  }
  if (loadDetails.StrapsNeededRequired) {
    specialEquipment.push('Straps');
  }
  if (loadDetails.TarpsRequired) {
    specialEquipment.push('Tarps');
  }

  return specialEquipment.join(', ') || false;
}

export const getBINLoadRequirements = (load: LoadType) => {
  const requirements = [];
  if (load.IsOverSizeLoad) {
    requirements.push('Oversized Load')
  }

  if (load.TwicNeeded) {
    requirements.push('TWIC')
  }

  if (load.IsHazmat) {
    requirements.push('Hazmat')
  }

  if (load.IsTeam) {
    requirements.push('Team')
  }

  if (load.DropTrailer || load.PickupApptTypeId === AppointmentType.DropTrailer || load.DeliveryApptTypeId === AppointmentType.DropTrailer) {
    requirements.push('Drop Trailer')
  }

  if (load.TankerEndorsement) {
    requirements.push('Tanker Endorsed')
  }

  if (load.NumberOfPickups > 1 || load.NumberOfDeliveries > 1) {
    requirements.push('Multistop')
  }

  return requirements.join(', ') || false;
}

export const getExcludeEquipment = (loadDetails: LoadType) => {
  const specialEquipment = [];

  if (loadDetails.FoodGradeCantHave) {
    specialEquipment.push('Food Grade');
  }
  if (loadDetails.HighCubeCantHave) {
    specialEquipment.push('High Cube');
  }
  if (loadDetails.LiftGateCantHave) {
    specialEquipment.push('Lift Gate');
  }
  if (loadDetails.LoadBarsCantHave) {
    specialEquipment.push('Load Bars');
  }
  if (loadDetails.PlateTrailerCantHave) {
    specialEquipment.push('Plate Trailer')
  }
  if (loadDetails.RollDoorsCantHave) {
    specialEquipment.push('Roll Doors');
  }
  if (loadDetails.StrapsNeededCantHave) {
    specialEquipment.push('Straps');
  }

  return specialEquipment.join(', ') || false;
}

export const getFormattedDateString = (dateString: string, locationIANACode?: string, stringFormat?: string) => {
  const formattedTime = moment.utc(dateString);
  let timezoneFormatted = '';

  if (locationIANACode) {
    timezoneFormatted = ` ${moment().tz(locationIANACode).format('z')}`
    formattedTime.tz(locationIANACode);
  }

  return `${formattedTime.format(stringFormat || LONG_FORMAT_TIME)}${timezoneFormatted}`;
};

export const getFormattedAppointmentDate = (earlyTime: string, lateTime: string, timezone?: string, shortFormat?: boolean) => {
  let timezoneFormated = ""

  if (timezone) {
    timezoneFormated = moment(earlyTime).tz(timezone).format('z');
  }

  const earlyTimeMoment = getRealTimezoneMoment(earlyTime, timezone);
  const lateTimeMoment = getRealTimezoneMoment(lateTime || earlyTime, timezone);

  let time = '';

  if (earlyTimeMoment.isSame(lateTimeMoment, 'm')) {
    time = `${earlyTimeMoment.format(shortFormat ? SHORT_FORMAT_TIME : LONG_FORMAT_TIME)} ${timezoneFormated}`;
  } else if (!earlyTimeMoment.isSame(lateTimeMoment, 'd')) {
    time = `${earlyTimeMoment.format(shortFormat ? SHORT_FORMAT_TIME : LONG_FORMAT_TIME)} ${timezoneFormated} - ${lateTimeMoment.format(shortFormat ? SHORT_FORMAT_TIME : LONG_FORMAT_TIME)} ${timezoneFormated}`;
  } else {
    time = `${earlyTimeMoment.format(shortFormat ? SHORT_FORMAT_TIME : LONG_FORMAT_TIME)} - ${lateTimeMoment.format('HH:mm')} ${timezoneFormated}`;
  }

  return time;
}

export const getFormattedAppointmentDateOnly = (earlyTime: string, lateTime: string, timezone: string, format?: string) => {
  const earlyTimeMoment = getRealTimezoneMoment(earlyTime, timezone);
  const lateTimeMoment = getRealTimezoneMoment(lateTime, timezone);
  let date = '';

  if (earlyTimeMoment.isSame(lateTimeMoment, 'd')) {
    date = earlyTimeMoment.format(format || LONG_FORMAT);
  } else {
    date = `${earlyTimeMoment.format(format || LONG_FORMAT)} - ${lateTimeMoment.format(format || LONG_FORMAT)}`;
  }

  return date;
}

export const getFormattedAppointmentTimeOnly = (earlyTime: string, lateTime: string, timezone: string) => {
  const timezoneFormated = moment(earlyTime).tz(timezone)?.format('z');
  const earlyTimeMoment = getRealTimezoneMoment(earlyTime, timezone);
  const lateTimeMoment = getRealTimezoneMoment(lateTime, timezone);
  let date = '';
  if (earlyTimeMoment.isSame(lateTimeMoment, 'm')) {
    date = `${earlyTimeMoment.format('HH:mm')} ${timezoneFormated}`;
  } else {
    date = `${earlyTimeMoment.format('HH:mm')} - ${lateTimeMoment.format('HH:mm')} ${timezoneFormated}`;
  }

  return date;
}

const postAcceptPendingOfferStatus = [OfferStatusId.ACCEPTED, OfferStatusId.EXPIRED, OfferStatusId.DECLINED, OfferStatusId.CANCELED, OfferStatusId.COMPLETED, OfferStatusId.CLOSED, OfferStatusId.RESERVED];
const amountChangeActions = [OfferStatusId.BIZ_COUNTER, OfferStatusId.LIVE];

export const getLastOfferAmount = (offer: OfferType, offerHistory: Array<OfferHistoryType>) => {

  if (postAcceptPendingOfferStatus.includes(offer.status)) {
    let lastAmountChange;

    for (let i = 0; i <= offerHistory.length; i++) {
      const curHistoryItem = offerHistory[i];
      if (offer.status !== OfferStatusId.CANCELED && amountChangeActions.includes(curHistoryItem.status)) {
        lastAmountChange = curHistoryItem;
        break;
      } else if (curHistoryItem.status === OfferStatusId.LIVE) {
        lastAmountChange = curHistoryItem
        break;
      }
      //This logic above is to return the most recent live offer when canceled for the edge case of a carrier canceling an offer with an active biz counter
    }

    return lastAmountChange?.amount || lastAmountChange?.bizCounterAmount || null;
  }

  return null;
}

export const getMostRecentOfferType = (offerHistory: Array<OfferHistoryType>) => {
  let mostRecentOfferType;
  for (let i = 0; i <= offerHistory.length; i++) {
    if (amountChangeActions.includes(offerHistory[i].status)) {
      mostRecentOfferType = offerHistory[i].status;
      break;
    }
  }
  return mostRecentOfferType;
}

export const isNotAvailableOrAssigned = (load: LoadType) => {
  return load.LoadStatusId && !(load.LoadStatusId === LoadStatusId.AVAILABLE || load.LoadStatusId === LoadStatusId.ASSIGNED);
}

export const getSpecialRequirements = (loadDetails: LoadType, specialServices: SpecialService[], removePermit?: boolean )=> {
  // filter special services with the ones that are added to the load
  const filteredServices = specialServices
    .filter(service => loadDetails.LoadEquipmentSpecialServices.some(loadSpecialService => loadSpecialService.LoadSpecialServiceId === service.LoadSpecialServiceId) && (removePermit ? service.LoadSpecialServiceId !== PERMIT_SPECIAL_SERVICE_ID : true));

  //add the quantity for special services with a quantity and permit value
  const filteredSpecialServicesWithValue = filteredServices.map(({Description, LoadSpecialServiceId}) => {
    const permitSpecialService = LoadSpecialServiceId === PERMIT_SPECIAL_SERVICE_ID;
    if (permitSpecialService) {
      const permitValue = loadDetails.PermitsRequired ? ` (${loadDetails.PermitsRequired})` : ''
      return `${Description}${permitValue}`
    }
    const specialServiceWithQuantity = LOAD_EQ_SPECIAL_SERVICES_IDS_WITH_VALUE.includes(LoadSpecialServiceId) && loadDetails.LoadEquipmentSpecialServices.find(service => service.LoadSpecialServiceId === LoadSpecialServiceId);
    const quantiy = specialServiceWithQuantity && specialServiceWithQuantity.Quantity ? ` (${specialServiceWithQuantity.Quantity})` : ''
    return `${Description}${quantiy}`
  });

  return filteredSpecialServicesWithValue.join(',\n') || false;
}

export const replaceFt = (description: string) => description.replace(',', ":").replace("ft", "(ft)")

export const isFromMyLoads = ()=> {
  if (window.opener) {
    try {
      const fromMyLoads = window.opener.location.pathname === ROUTES.myLoads
      return fromMyLoads
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e)
      return false
    }
  }
  return false
}
